/**
 * RevSlider Engine v2
 * -------------------
 * Fiecare layer ([data-anim]) e controlat complet din 6 atribute data-*,
 * toate mapate 1:1 pe dropdown-uri din Theme Editor (schema Liquid):
 *
 *   data-anim              -> tipul mișcării (fade-up, zoom-in, slide-left...)
 *   data-anim-ease         -> curba de easing (linear, ease-out, elastic, bounce...)
 *   data-anim-trigger      -> CÂND pornește (load, scroll, hover, click)
 *   data-anim-repeat       -> o dată sau de fiecare dată
 *   data-anim-delay        -> întârziere în ms
 *   data-anim-duration     -> durată în secunde
 *
 * Nimic din codul de mai jos nu trebuie modificat ca să schimbi
 * comportamentul unui layer — totul se schimbă din Theme Editor.
 */

(function () {
  "use strict";

  // FIX: browserul încearcă să restaureze automat poziția de scroll de
  // dinainte de refresh, DAR o face înainte ca GSAP să calculeze din nou
  // înălțimea reală a paginii (cu pin-spacer-ul scenei de scroll inclus).
  // Rezultatul: la refresh, userul e aruncat mai jos decât ar trebui,
  // direct în secțiunea următoare. Dezactivăm restaurarea automată —
  // pagina va porni mereu de sus la refresh, ca la orice site normal.
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  if (typeof gsap === "undefined") {
    console.warn("[RevSlider] GSAP nu este încărcat. Slider-ul nu va anima.");
    return;
  }
  if (typeof ScrollTrigger !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
  }

  var reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // ---- Dicționar de tipuri de mișcare (starea "from") ----
  var ANIMATIONS = {
    "fade-in": { opacity: 0 },
    "fade-up": { opacity: 0, y: 40 },
    "fade-down": { opacity: 0, y: -40 },
    "slide-left": { opacity: 0, x: 60 },
    "slide-right": { opacity: 0, x: -60 },
    "zoom-in": { opacity: 0, scale: 0.85 },
    "zoom-out": { opacity: 0, scale: 1.15 },
    none: {},
  };
  function getAnimProps(name) {
    return Object.assign({}, ANIMATIONS[name] || ANIMATIONS["fade-up"]);
  }

  // ---- Dicționar de curbe de easing (nume prietenos -> GSAP ease) ----
  var EASES = {
    linear: "none",
    "ease-out": "power2.out",
    "ease-in-out": "power2.inOut",
    elastic: "elastic.out(1, 0.5)",
    bounce: "bounce.out",
    back: "back.out(1.7)",
  };
  function getEase(name) {
    return EASES[name] || EASES["ease-out"];
  }

  // ---- Citește setările unui layer din data-attribute-uri ----
  function readLayerConfig(el) {
    return {
      anim: el.dataset.anim || "fade-up",
      ease: getEase(el.dataset.animEase),
      trigger: el.dataset.animTrigger || "load",
      repeat: el.dataset.animRepeat || "always", // "once" | "always"
      delay: parseFloat(el.dataset.animDelay || "0") / 1000,
      duration: parseFloat(el.dataset.animDuration || "0.7"),
    };
  }

  // Rulează animația de intrare pentru un singur layer, o singură dată
  // la apelare (funcție de bază refolosită de toate cele 4 trigger-e).
  function playLayer(el, cfg) {
    if (reducedMotion || cfg.anim === "none") {
      gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
      return;
    }
    gsap.killTweensOf(el);
    gsap.fromTo(
      el,
      getAnimProps(cfg.anim),
      {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: cfg.duration,
        ease: cfg.ease,
        delay: cfg.delay,
      }
    );
  }

  // Ascunde layer-ul înainte să pornească (stare "from"), ca să nu
  // apară o clipă vizibil înainte de trigger (mai ales la hover/click).
  function primeLayer(el, cfg) {
    if (reducedMotion || cfg.anim === "none") return;
    gsap.set(el, getAnimProps(cfg.anim));
  }

  // =====================================================================
  // Setup pe tip de trigger
  // =====================================================================
  function setupLoadTrigger(el, cfg) {
    var slide = el.closest(".revslider__slide");
    if (slide) {
      // Layer într-un slide: "load" = de fiecare dată când slide-ul devine activ
      // (sau o singură dată, dacă repeat === "once")
      var playedOnce = false;
      primeLayer(el, cfg);
      slide.addEventListener("revslide:active", function () {
        if (cfg.repeat === "once" && playedOnce) return;
        playedOnce = true;
        playLayer(el, cfg);
      });
    } else {
      // Layer independent (oriunde altundeva în temă): animă la încărcarea paginii
      primeLayer(el, cfg);
      requestAnimationFrame(function () {
        playLayer(el, cfg);
      });
    }
  }

  function setupScrollTrigger(el, cfg) {
    if (typeof ScrollTrigger === "undefined") return;
    if (reducedMotion || cfg.anim === "none") {
      gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
      return;
    }
    var toggleActions =
      cfg.repeat === "once" ? "play none none none" : "restart none restart none";

    gsap.fromTo(
      el,
      getAnimProps(cfg.anim),
      {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: cfg.duration,
        ease: cfg.ease,
        delay: cfg.delay,
        scrollTrigger: {
          trigger: el,
          start: el.dataset.animScrollStart || "top 85%",
          toggleActions: toggleActions,
        },
      }
    );
  }

  function setupHoverTrigger(el, cfg) {
    primeLayer(el, cfg);
    var playedOnce = false;
    el.addEventListener("mouseenter", function () {
      if (cfg.repeat === "once" && playedOnce) return;
      playedOnce = true;
      playLayer(el, cfg);
    });
  }

  function setupClickTrigger(el, cfg) {
    primeLayer(el, cfg);
    var playedOnce = false;
    el.addEventListener("click", function () {
      if (cfg.repeat === "once" && playedOnce) return;
      playedOnce = true;
      playLayer(el, cfg);
    });
  }

  // Se aplică o singură dată la fiecare layer, la inițializare.
  function initLayer(el) {
    if (el.__revLayerInit) return; // evită dublă inițializare la re-render
    el.__revLayerInit = true;

    var cfg = readLayerConfig(el);
    switch (cfg.trigger) {
      case "scroll":
        setupScrollTrigger(el, cfg);
        break;
      case "hover":
        setupHoverTrigger(el, cfg);
        break;
      case "click":
        setupClickTrigger(el, cfg);
        break;
      case "load":
      default:
        setupLoadTrigger(el, cfg);
        break;
    }
  }

  function initAllLayers() {
    document.querySelectorAll("[data-anim]").forEach(initLayer);
  }

  // =====================================================================
  // RevSlider — logica de slide-uri (independentă de trigger-ul layerelor)
  // =====================================================================
  function RevSlider(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(
      root.querySelectorAll(".revslider__slide")
    );
    this.dotsWrap = root.querySelector(".revslider__dots");
    this.prevBtn = root.querySelector(".revslider__arrow--prev");
    this.nextBtn = root.querySelector(".revslider__arrow--next");

    this.current = 0;
    this.autoplay = root.dataset.autoplay === "true";
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed || "6000", 10);
    this.loop = root.dataset.loop !== "false";
    this.timer = null;

    if (!this.slides.length) return;

    this._buildDots();
    this._bindControls();
    this._goTo(0, true);
    if (this.autoplay && this.slides.length > 1) this._startAutoplay();

    root.addEventListener("mouseenter", this._stopAutoplay.bind(this));
    root.addEventListener("mouseleave", this._startAutoplay.bind(this));
  }

  RevSlider.prototype._buildDots = function () {
    if (!this.dotsWrap) return;
    var self = this;
    this.slides.forEach(function (_, i) {
      var dot = document.createElement("button");
      dot.className = "revslider__dot";
      dot.setAttribute("aria-label", "Mergi la slide " + (i + 1));
      dot.addEventListener("click", function () {
        self._goTo(i);
      });
      self.dotsWrap.appendChild(dot);
    });
  };

  RevSlider.prototype._bindControls = function () {
    var self = this;
    if (this.prevBtn)
      this.prevBtn.addEventListener("click", function () {
        self._goTo(self.current - 1);
      });
    if (this.nextBtn)
      this.nextBtn.addEventListener("click", function () {
        self._goTo(self.current + 1);
      });
  };

  RevSlider.prototype._startAutoplay = function () {
    if (!this.autoplay || this.slides.length < 2) return;
    this._stopAutoplay();
    var self = this;
    this.timer = setInterval(function () {
      self._goTo(self.current + 1);
    }, this.autoplaySpeed);
  };

  RevSlider.prototype._stopAutoplay = function () {
    if (this.timer) clearInterval(this.timer);
  };

  RevSlider.prototype._goTo = function (index, isInit) {
    var total = this.slides.length;
    if (index < 0) index = this.loop ? total - 1 : 0;
    if (index >= total) index = this.loop ? 0 : total - 1;

    var prevSlide = this.slides[this.current];
    var nextSlide = this.slides[index];

    if (!isInit && prevSlide && prevSlide !== nextSlide) {
      prevSlide.classList.remove("is-active");
      if (!reducedMotion) {
        gsap.to(prevSlide, { opacity: 0, duration: 0.5, ease: "power2.inOut" });
      } else {
        prevSlide.style.opacity = 0;
      }
    }

    this.current = index;
    this._updateDots();

    nextSlide.classList.add("is-active");
    if (!reducedMotion) {
      gsap.fromTo(
        nextSlide,
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: "power2.out" }
      );
    } else {
      gsap.set(nextSlide, { opacity: 1 });
    }

    // anunță layerele din acest slide că slide-ul tocmai a devenit activ
    // (folosit de layerele cu trigger = "load")
    nextSlide.dispatchEvent(new CustomEvent("revslide:active"));
  };

  RevSlider.prototype._updateDots = function () {
    if (!this.dotsWrap) return;
    var dots = this.dotsWrap.querySelectorAll(".revslider__dot");
    dots.forEach(function (d, i) {
      d.classList.toggle("is-active", i === this.current);
    }, this);
  };

  // =====================================================================
  // Parallax generic (neschimbat) — pt fundal / imagini cu data-revspeed
  // =====================================================================
  function initParallax() {
    if (typeof ScrollTrigger === "undefined" || reducedMotion) return;
    document.querySelectorAll("[data-revspeed]").forEach(function (el) {
      if (el.__revParallaxInit) return;
      el.__revParallaxInit = true;
      var speed = parseFloat(el.dataset.revspeed || "0.3");
      gsap.to(el, {
        yPercent: speed * 100,
        ease: "none",
        scrollTrigger: {
          trigger: el.closest("[data-revspeed-scope]") || el.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });
  }

  // =====================================================================
  // ScrollScene — narativă legată 100% de scroll (cer -> nori -> tranziție
  // rapidă -> pământ -> copaci).
  //
  // REVIZUIT COMPLET: NU mai folosim ScrollTrigger "pin: true" (care
  // depinde de position:fixed — exact mecanismul care s-a dovedit fragil
  // în combinație cu tema). Acum:
  //   - "rămânerea pe loc" a scenei = position:sticky NATIV (CSS pur)
  //   - progresul animației = calculat MANUAL, citind direct scroll-ul,
  //     fără GSAP ScrollTrigger, fără pin, fără position:fixed nicăieri.
  // Practic, nu mai există mecanismul care se tot strica — l-am eliminat,
  // nu l-am "reparat".
  // =====================================================================
  function ScrollScene(root) {
    // "root" e wrapper-ul înalt (.scrollscene-wrap); scena vizuală propriu-zisă
    // (.scrollscene, position:sticky) e un copil direct al lui.
    var sky = root.querySelector(".scrollscene__panel--sky");
    var ground = root.querySelector(".scrollscene__panel--ground");
    var clouds = root.querySelector('[data-scene-layer="clouds"]');
    var trees = root.querySelector('[data-scene-layer="trees"]');
    var skyHeading = root.querySelector('[data-scene-layer="sky-heading"]');
    var groundHeading = root.querySelector('[data-scene-layer="ground-heading"]');

    if (reducedMotion) {
      [clouds, trees, skyHeading, groundHeading].forEach(function (el) {
        if (el) gsap.set(el, { opacity: 1, y: 0 });
      });
      if (ground) gsap.set(ground, { opacity: 1, y: "0%" });
      if (sky) gsap.set(sky, { opacity: 1, y: "0%" });
      return;
    }

    function clamp01(v) {
      return Math.max(0, Math.min(1, v));
    }

    // Curbă de easing (ease-in-out cubic) — aplicată pe progresul LOCAL
    // al fiecărei faze, ca mișcarea să accelereze/încetinească lin, nu
    // liniar/mecanic.
    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    // Aplică starea vizuală pentru un progres p (0 = start, 1 = final).
    function applyProgress(p) {
      // FAZA 1 (0 -> 0.4): apar norii + titlul de pe cer
      var p1 = easeInOutCubic(clamp01(p / 0.4));
      if (clouds) gsap.set(clouds, { opacity: p1, y: 30 * (1 - p1) });
      if (skyHeading) gsap.set(skyHeading, { opacity: p1, y: 20 * (1 - p1) });

      // FAZA 2 (0.4 -> 0.55): tranziția RAPIDĂ cer -> pământ
      var p2 = easeInOutCubic(clamp01((p - 0.4) / 0.15));
      if (sky) gsap.set(sky, { opacity: 1 - p2, y: -15 * p2 + "%" });
      if (ground) gsap.set(ground, { opacity: p2, y: 15 * (1 - p2) + "%" });

      // FAZA 3 (0.55 -> 1): apar copacii + titlul de pe pământ
      var p3 = easeInOutCubic(clamp01((p - 0.55) / 0.45));
      if (trees) gsap.set(trees, { opacity: p3, y: 30 * (1 - p3) });
      if (groundHeading) gsap.set(groundHeading, { opacity: p3, y: 20 * (1 - p3) });
    }

    // ---------------------------------------------------------------------
    // NETEZIRE: în loc să sărim direct la valoarea exactă de scroll (ceea
    // ce poate simți "mecanic", mai ales pe trackpad cu scroll în salturi),
    // rulăm un loop continuu (requestAnimationFrame) care interpolează LIN
    // ("lerp") de la valoarea afișată curent către valoarea țintă (unde
    // ești tu, real, cu scroll-ul). Rezultă o mișcare catifelată, care
    // "prinde din urmă" scroll-ul lin, nu instant.
    // ---------------------------------------------------------------------
    var targetProgress = 0;
    var currentProgress = 0;
    var smoothing = 0.09; // 0 = nu se mișcă deloc; 1 = instant (fără netezire)

    function measureTarget() {
      var rect = root.getBoundingClientRect();
      var scrollableDistance = rect.height - window.innerHeight;
      targetProgress = scrollableDistance <= 0 ? 1 : clamp01(-rect.top / scrollableDistance);
    }

    function loop() {
      measureTarget();
      var delta = targetProgress - currentProgress;
      if (Math.abs(delta) < 0.0005) {
        currentProgress = targetProgress;
      } else {
        currentProgress += delta * smoothing;
      }
      applyProgress(currentProgress);
      requestAnimationFrame(loop);
    }

    measureTarget();
    currentProgress = targetProgress; // fără "alunecare" falsă la încărcarea paginii
    applyProgress(currentProgress);
    requestAnimationFrame(loop);
  }

  // =====================================================================
  // ProductReveal — jucărie statică (centrată la poziția ei) + un strat
  // (ex: roci) care "urcă" din direcția aleasă pe măsură ce userul
  // scrollează. Aceeași tehnică validată ca la ScrollScene: sticky CSS +
  // calcul manual de progres + netezire — fără GSAP pin, fără
  // position:fixed nicăieri.
  // =====================================================================
  function ProductReveal(root) {
    var layers = root.querySelectorAll(".productreveal__slot");
    var MOBILE_QUERY = "(max-width: 749px)";

    // Aplică top/left/height corecte (desktop SAU mobil, citite din
    // atributele data-pos-*) direct ca stil inline, din JS — nu mai
    // depindem de un <style> cu media query + ID de secțiune, care s-a
    // dovedit fragil (mismatch de ID greu de depanat). JS-ul verifică
    // simplu, cu window.matchMedia, ce variantă să aplice, GARANTAT.
    function applyResponsivePosition(el) {
      var isMobile = window.matchMedia(MOBILE_QUERY).matches;
      var prefix = isMobile ? "Mobile" : "Desktop";
      var wantedBreakpoint = isMobile ? "mobile" : "desktop";

      // Arătăm DOAR imaginea corectă din interiorul cadrului (desktop
      // sau mobil) — fiecare are propria proporție, propriul spațiu gol
      // intenționat, independent de cealaltă.
      var activeImg = null;
      el.querySelectorAll(".productreveal__img").forEach(function (img) {
        var isMatch = img.dataset.breakpoint === wantedBreakpoint;
        img.style.display = isMatch ? "block" : "none";
        if (isMatch) activeImg = img;
      });
      // Dacă nu există imaginea specifică pentru acest breakpoint
      // (ex: nu s-a încărcat variantă de mobil), folosim orice e disponibil.
      if (!activeImg) {
        activeImg = el.querySelector(".productreveal__img");
        if (activeImg) activeImg.style.display = "block";
      }

      var top = el.dataset["posTop" + prefix];
      var left = el.dataset["posLeft" + prefix];
      var height = el.dataset["posHeight" + prefix];
      var aspectRatio = activeImg ? parseFloat(activeImg.dataset.aspectRatio || "1") : 1;

      if (top !== undefined) el.style.top = top + "%";
      if (left !== undefined) el.style.left = left + "%";
      if (height !== undefined) {
        el.style.height = height + "vh";
        // Lățimea cadrului = înălțime * raportul REAL al imaginii afișate
        // acum (desktop sau mobil) — fiecare variantă își păstrează
        // propriul spațiu gol intenționat din jurul obiectului.
        el.style.width = "calc(" + height + "vh * " + aspectRatio + ")";
      }
    }

    function applyAllPositions() {
      layers.forEach(applyResponsivePosition);
      applyTitlePosition();
      applyPointPositions();
    }

    // Punctele au nevoie doar de top/left responsive (ca titlul), nu de
    // height/aspect-ratio ca sloturile cu imagini.
    var points = root.querySelectorAll(".productreveal__point");
    function applyPointPositions() {
      var isMobile = window.matchMedia(MOBILE_QUERY).matches;
      var prefix = isMobile ? "Mobile" : "Desktop";
      points.forEach(function (pt) {
        var top = pt.dataset["posTop" + prefix];
        var left = pt.dataset["posLeft" + prefix];
        if (top !== undefined) pt.style.top = top + "%";
        if (left !== undefined) pt.style.left = left + "%";

        // Lungimea liniei (diagonal + orizontal) — separat desktop/mobil
        var diag = isMobile ? pt.dataset.diagMobile : pt.dataset.diagDesktop;
        var horiz = isMobile ? pt.dataset.horizMobile : pt.dataset.horizDesktop;
        if (diag) pt.style.setProperty("--pr-diag-len", diag + "px");
        if (horiz) pt.style.setProperty("--pr-horiz-len", horiz + "px");

        // Mărimea textului — separat desktop/mobil
        var label = pt.querySelector(".productreveal__point-label");
        if (label) {
          var size = isMobile ? label.dataset.sizeMobile : label.dataset.sizeDesktop;
          if (size) label.style.fontSize = size + "px";
        }
      });
    }

    // Titlul e un element separat (text, nu imagine) — se poziționează
    // la fel (top/left responsive), dar mărimea lui e font-size, nu
    // height+aspect-ratio ca la sloturile cu imagini. Cubul din spatele
    // lui folosește ACEEAȘI poziție, dimensiune calculată din font-size.
    var title = root.querySelector(".productreveal__title");
    var titleCube = root.querySelector(".productreveal__title-cube");

    function applyTitlePosition() {
      if (!title) return;
      var isMobile = window.matchMedia(MOBILE_QUERY).matches;
      var prefix = isMobile ? "Mobile" : "Desktop";
      var top = title.dataset["posTop" + prefix];
      var left = title.dataset["posLeft" + prefix];
      var size = title.dataset["posSize" + prefix];
      if (top !== undefined) title.style.top = top + "%";
      if (left !== undefined) title.style.left = left + "%";
      if (size !== undefined) title.style.fontSize = size + "px";

      if (titleCube && top !== undefined && left !== undefined && size !== undefined) {
        titleCube.style.top = top + "%";
        titleCube.style.left = left + "%";
        var cubeSide = parseFloat(size) * 0.45;
        titleCube.style.width = cubeSide + "px";
        titleCube.style.height = cubeSide + "px";
      }
    }

    applyAllPositions();

    // Centrăm FIECARE layer (static sau animat) la poziția lui top/left
    // — centrarea se face din GSAP (xPercent/yPercent), ca să nu intre
    // în conflict cu animația de reveal a rocilor.
    layers.forEach(function (el) {
      gsap.set(el, { xPercent: -50, yPercent: -50 });
    });
    if (title) gsap.set(title, { xPercent: -50, yPercent: -50 });
    if (titleCube) gsap.set(titleCube, { xPercent: -50, yPercent: -50 });
    if (title) initTitleScramble(title);

    // Dacă userul rotește telefonul sau redimensionează fereastra peste
    // pragul de 749px, re-aplicăm poziția corectă pentru noua lățime.
    var mq = window.matchMedia(MOBILE_QUERY);
    var onBreakpointChange = function () {
      applyAllPositions();
    };
    if (mq.addEventListener) {
      mq.addEventListener("change", onBreakpointChange);
    } else if (mq.addListener) {
      mq.addListener(onBreakpointChange); // fallback Safari mai vechi
    }

    var reveal = root.querySelector('[data-reveal-layer="rocks"]');
    if (!reveal) return;

    var direction = reveal.dataset.revealDirection || "up";
    var distance = parseFloat(reveal.dataset.revealDistance || "100");

    // Traducem direcția aleasă din schema într-un offset x/y de PORNIRE
    // (unde stă rocile înainte să apară) — poziția finală e mereu 0,0
    // (adică exact acolo unde ai poziționat-o cu sliderele din editor).
    var startOffset = { x: 0, y: 0 };
    if (direction === "up") startOffset = { x: 0, y: distance };
    if (direction === "down") startOffset = { x: 0, y: -distance };
    if (direction === "left") startOffset = { x: distance, y: 0 };
    if (direction === "right") startOffset = { x: -distance, y: 0 };

    if (reducedMotion) {
      gsap.set(reveal, { opacity: 1, x: 0, y: 0 });
      if (title) gsap.set(title, { opacity: 1, scale: 1 });
      if (titleCube) gsap.set(titleCube, { opacity: 0, scale: 0 });
      points.forEach(function (pt) {
        pt.style.opacity = 1;
        pt.classList.add("is-open");
      });
      return;
    }

    function clamp01(v) {
      return Math.max(0, Math.min(1, v));
    }
    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    // Stare inițială: titlul e "ascuns în cub" (minuscul, invizibil),
    // cubul e ȘI EL ascuns — apare abia când userul începe efectiv să
    // dea scroll, nu de la bun început.
    if (title) gsap.set(title, { opacity: 0, scale: 0.06, transformOrigin: "50% 50%" });
    if (titleCube) gsap.set(titleCube, { opacity: 0, scale: 1, transformOrigin: "50% 50%" });

    function applyProgress(p) {
      var eased = easeOutCubic(clamp01(p));
      gsap.set(reveal, {
        opacity: eased,
        x: startOffset.x * (1 - eased),
        y: startOffset.y * (1 - eased),
      });
      if (title) {
        // titlul CREȘTE din cub (nu alunecă de jos) — pornește minuscul,
        // ajunge la mărimea lui reală exact când rocile sunt complet vizibile
        gsap.set(title, {
          opacity: eased,
          scale: 0.06 + eased * 0.94,
        });
      }
      if (titleCube) {
        // cubul APARE rapid (primele 6% din tranziție), apoi se
        // "consumă" (următoarele până la 55%), ca senzația să fie de
        // "textul iese din cub", nu de "cele două cresc/scad în
        // paralel". La progres 0 (înainte de orice scroll), NIMIC nu
        // e vizibil — nici cubul, nici textul.
        var cubeAppear = clamp01(eased / 0.06);
        var cubeShrink = clamp01(eased / 0.55);
        gsap.set(titleCube, {
          opacity: cubeAppear * (1 - cubeShrink),
          scale: 1 - cubeShrink * 0.75,
        });
      }
      // Punctele apar (fade-in) ȘI liniile lor se deschid automat, în
      // cascadă (nu mai e nevoie de click) — fiecare punct se
      // "developează" la un prag ușor diferit de progres, ca să pară
      // o secvență, nu ca totul apare deodată.
      points.forEach(function (pt, index) {
        pt.style.opacity = eased;
        var openThreshold = 0.55 + index * 0.12; // 0.55, 0.67, 0.79...
        if (eased >= openThreshold) {
          pt.classList.add("is-open");
        } else {
          pt.classList.remove("is-open");
        }
      });
    }

    var targetProgress = 0;
    var currentProgress = 0;
    var smoothing = 0.09;

    function measureTarget() {
      var rect = root.getBoundingClientRect();
      var scrollableDistance = rect.height - window.innerHeight;
      targetProgress = scrollableDistance <= 0 ? 1 : clamp01(-rect.top / scrollableDistance);
    }

    function loop() {
      measureTarget();
      var delta = targetProgress - currentProgress;
      currentProgress = Math.abs(delta) < 0.0005 ? targetProgress : currentProgress + delta * smoothing;
      applyProgress(currentProgress);
      requestAnimationFrame(loop);
    }

    measureTarget();
    currentProgress = targetProgress;
    applyProgress(currentProgress);
    requestAnimationFrame(loop);
  }


  // =====================================================================
  // Efect de UMPLERE la hover/atingere pe titlu: fiecare literă devine
  // 2 straturi suprapuse perfect — unul cu culoarea de bază (mereu
  // vizibil), unul verde (ascuns cu clip-path). Când mouse-ul (sau
  // degetul, pe mobil) trece la mai puțin de X pixeli de o literă,
  // stratul verde se "developează" de jos în sus (clip-path animat),
  // cu o textură fină de damier (mask-image), ca umplerea să pară din
  // pixeli/cuburi mici, nu vopsea solidă. Se dezactivează complet dacă
  // userul preferă animații reduse.
  // =====================================================================
  function initTitleScramble(title) {
    if (!title || reducedMotion) return;

    var hoverColor = title.dataset.hoverColor || "#baff5c";
    var radius = parseFloat(title.dataset.hoverRadius || "55");
    title.style.setProperty("--pr-hover-color", hoverColor);

    var originalText = title.textContent;
    title.setAttribute("aria-label", originalText);
    title.textContent = "";

    var chars = [];

    originalText.split("").forEach(function (ch) {
      var wrap = document.createElement("span");
      wrap.className = "productreveal__char-wrap";

      var base = document.createElement("span");
      base.className = "productreveal__char-base";
      base.textContent = ch === " " ? "\u00A0" : ch;

      var fill = document.createElement("span");
      fill.className = "productreveal__char-fill";
      fill.setAttribute("aria-hidden", "true");
      fill.textContent = ch === " " ? "\u00A0" : ch;

      wrap.appendChild(base);
      wrap.appendChild(fill);
      title.appendChild(wrap);

      chars.push({ el: wrap, original: ch, active: false, cx: 0, cy: 0 });
    });

    function measureRects() {
      chars.forEach(function (c) {
        var r = c.el.getBoundingClientRect();
        c.cx = r.left + r.width / 2;
        c.cy = r.top + r.height / 2;
      });
    }
    measureRects();
    window.addEventListener("resize", function () {
      requestAnimationFrame(measureRects);
    });

    function activate(c) {
      if (c.active || c.original === " ") return;
      c.active = true;
      c.el.classList.add("is-active");
    }

    function deactivate(c) {
      if (!c.active) return;
      c.active = false;
      c.el.classList.remove("is-active");
    }

    var ticking = false;
    function handlePointer(x, y) {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        chars.forEach(function (c) {
          var dx = c.cx - x;
          var dy = c.cy - y;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < radius) {
            activate(c);
          } else {
            deactivate(c);
          }
        });
      });
    }

    function clearAll() {
      chars.forEach(deactivate);
    }

    // Desktop: mouse
    title.addEventListener("pointermove", function (e) {
      handlePointer(e.clientX, e.clientY);
    });
    title.addEventListener("pointerleave", clearAll);

    // Mobil: degetul care alunecă peste titlu — EXACT același
    // comportament ca hover-ul de pe desktop.
    title.addEventListener(
      "touchmove",
      function (e) {
        if (e.touches && e.touches[0]) {
          handlePointer(e.touches[0].clientX, e.touches[0].clientY);
        }
      },
      { passive: true }
    );
    title.addEventListener("touchend", clearAll);
    title.addEventListener("touchcancel", clearAll);
  }

  // nu mai memorează nimic calculat o singură dată la început.
  // ---------------------------------------------------------------------
  function initAll() {
    document.querySelectorAll("[data-revslider]").forEach(function (root) {
      if (!root.__revsliderInstance) {
        root.__revsliderInstance = new RevSlider(root);
      }
    });
    document.querySelectorAll("[data-scrollscene]").forEach(function (root) {
      if (!root.__scrollSceneInit) {
        root.__scrollSceneInit = true;
        new ScrollScene(root);
      }
    });
    document.querySelectorAll("[data-productreveal]").forEach(function (root) {
      if (!root.__productRevealInit) {
        root.__productRevealInit = true;
        new ProductReveal(root);
      }
    });
    initAllLayers();
    initParallax();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Editorul de teme Shopify re-randează secțiuni live, fără reload —
  // trebuie reinițializat manual în acel caz.
  document.addEventListener("shopify:section:load", initAll);

  window.RevSlider = RevSlider;
})();